import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, from } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { ClientJS } from 'clientjs';
import * as ed from '@noble/ed25519';
import { AppConstants } from '../commons/app-constants';
import { SecureKeyService } from './secure-key.service';
import { offlineDb, summarizeUnsynced } from '../offline/offline-db';
import { isOfflineCapableNative } from '../commons/utils/platform';

export interface StoredLease {
  userId: number;
  tenantId: number;
  deviceId: string;
  issuedAtEpochMillis: number;
  graceUntilEpochMillis: number;
  signatureBase64: string;
  publicKeyBase64: string;
}

export interface OfflineLeaseCheckResult {
  valid: boolean;
  withinGrace: boolean;
  reason?: 'no-lease' | 'corrupt' | 'bad-signature' | 'clock-rollback' | 'grace-expired';
}

const LEASE_STORAGE_KEY = 'll_offline_lease';
// offline-db `notices` key — see keepPreviousOwnerLease().
const PREVIOUS_OWNER_LEASE_KEY = 'previousOwnerLease';
const MAX_WALL_CLOCK_KEY = 'll_offline_max_wall_clock';
// A device that reports a wall clock more than this far behind the latest clock reading
// we've ever seen is treated as tampered (rolled back to stay inside the grace window).
const CLOCK_ROLLBACK_TOLERANCE_MILLIS = 5 * 60 * 1000;

// Fetches and locally verifies the signed offline-grace lease from GET /api/v1/sync/heartbeat
// (see SyncController + LeaseSigningKeyService on the backend). check() can run fully offline —
// it verifies the Ed25519 signature and grace window using only locally stored data.
@Injectable({ providedIn: 'root' })
export class OfflineLeaseService {
  private readonly http = inject(HttpClient);
  private readonly secureKeyService = inject(SecureKeyService);

  refresh(): Observable<void> {
    const deviceId = this.getDeviceId();
    const headers = new HttpHeaders({ deviceId });
    return this.http
      .get<StoredLease>(`${AppConstants.API_URL}api/v1/sync/heartbeat`, { headers, withCredentials: true })
      .pipe(
        switchMap((lease) => from(this.keepPreviousOwnerLease(lease).catch(() => undefined)).pipe(map(() => lease))),
        map((lease) => {
          this.secureKeyService.setItem(LEASE_STORAGE_KEY, JSON.stringify(lease));
          // Deliberately routed through the SAME guarded path detectClockRollback() uses
          // internally (only overwrites if this is a new maximum) rather than an unconditional
          // recordWallClock() — an unconditional write here was a real, confirmed hole: an
          // attacker could roll the device clock back, then complete one ordinary online
          // refresh (the server doesn't validate the client's clock when issuing a lease) to
          // silently lower the recorded high-water mark to the rolled-back value. Every
          // subsequent offline check would then compare against that lowered watermark instead
          // of the true one, defeating rollback detection entirely — since graceUntilEpochMillis
          // is a real future server timestamp, a sufficiently-rolled-back clock satisfies
          // `now <= graceUntilEpochMillis` indefinitely, making the 7-day offline cap
          // unenforceable rather than merely inaccurate.
          this.recordWallClockIfHigher(Date.now());
          this.cachedResult = null; // new lease fetched — next check() must re-verify it fresh
        }),
      );
  }

  /** OFFLINE_PLAN.md 1.5 (2026-09-29). A different user's lease is about to replace the stored one while
   *  the previous user's offline changes are still waiting — set the previous lease aside first. It is the
   *  only proof the server accepts for telling that user "your changes were removed" (the new login's
   *  lease refresh can run before the different-user check that removes them). */
  private async keepPreviousOwnerLease(next: StoredLease): Promise<void> {
    if (!isOfflineCapableNative()) return;
    const raw = await this.secureKeyService.getItem(LEASE_STORAGE_KEY);
    if (!raw) return;
    let current: StoredLease;
    try { current = JSON.parse(raw); } catch { return; }
    if (!current?.userId || current.userId === next.userId) return;
    if ((await summarizeUnsynced()).count === 0) return;
    await offlineDb.notices.put({ key: PREVIOUS_OWNER_LEASE_KEY, value: raw });
  }

  /** The lease to prove the previous user's identity with: the one set aside above, else the stored one.
   *  Clears the set-aside copy (used once). */
  async takeLeaseForRemovedNotice(): Promise<StoredLease | null> {
    if (!isOfflineCapableNative()) return null;
    const kept = await offlineDb.notices.get(PREVIOUS_OWNER_LEASE_KEY);
    if (kept) {
      await offlineDb.notices.delete(PREVIOUS_OWNER_LEASE_KEY);
      try { return JSON.parse(kept.value) as StoredLease; } catch { /* fall through */ }
    }
    const raw = await this.secureKeyService.getItem(LEASE_STORAGE_KEY);
    if (!raw) return null;
    try { return JSON.parse(raw) as StoredLease; } catch { return null; }
  }

  // check() is called fresh on EVERY guarded navigation while offline (AuthSessionGuard's catchError
  // fallback) — each call does 2-3 native secure-storage round-trips (Android Keystore-backed,
  // measurably slow, sometimes involving a device-auth challenge) plus an Ed25519 verification.
  // Repeating that on every single page visit is almost certainly what was making the whole
  // app feel frozen for a few seconds on every offline navigation. The lease's own grace
  // window is days long, so re-verifying on every navigation buys nothing security-wise —
  // caching the result for a short window eliminates the repetition without weakening it
  // (a genuine revocation/expiry is still caught within CACHE_TTL_MILLIS).
  private cachedResult: OfflineLeaseCheckResult | null = null;
  private cachedAt = 0;
  private static readonly CACHE_TTL_MILLIS = 60_000;

  async check(): Promise<OfflineLeaseCheckResult> {
    const now = Date.now();
    if (this.cachedResult && (now - this.cachedAt) < OfflineLeaseService.CACHE_TTL_MILLIS) {
      return this.cachedResult;
    }
    const result = await this.performCheck();
    this.cachedResult = result;
    this.cachedAt = now;
    return result;
  }

  private async performCheck(): Promise<OfflineLeaseCheckResult> {
    const raw = await this.secureKeyService.getItem(LEASE_STORAGE_KEY);
    if (!raw) return { valid: false, withinGrace: false, reason: 'no-lease' };

    let lease: StoredLease;
    try {
      lease = JSON.parse(raw);
    } catch {
      return { valid: false, withinGrace: false, reason: 'corrupt' };
    }

    const signatureValid = await this.verifySignature(lease);
    if (!signatureValid) return { valid: false, withinGrace: false, reason: 'bad-signature' };

    const now = Date.now();
    const rollbackDetected = await this.detectClockRollback(now);
    if (rollbackDetected) return { valid: true, withinGrace: false, reason: 'clock-rollback' };

    const withinGrace = now <= lease.graceUntilEpochMillis;
    return { valid: true, withinGrace, reason: withinGrace ? undefined : 'grace-expired' };
  }

  async clear(): Promise<void> {
    await this.secureKeyService.removeItem(LEASE_STORAGE_KEY);
    await this.secureKeyService.removeItem(MAX_WALL_CLOCK_KEY);
    this.cachedResult = null;
  }

  private getDeviceId(): string {
    return new ClientJS().getFingerprint()?.toString() ?? '';
  }

  // Must build byte-identical output to SyncController.buildCanonicalLeaseString — do not
  // change the field order/delimiter without a matching backend change.
  private async verifySignature(lease: StoredLease): Promise<boolean> {
    const canonical = `${lease.userId}|${lease.tenantId}|${lease.deviceId ?? ''}|${lease.issuedAtEpochMillis}|${lease.graceUntilEpochMillis}`;
    const message = new TextEncoder().encode(canonical);
    try {
      const signature = base64ToBytes(lease.signatureBase64);
      const publicKey = base64ToBytes(lease.publicKeyBase64);
      return await ed.verifyAsync(signature, message, publicKey);
    } catch {
      return false;
    }
  }

  // The ONLY place MAX_WALL_CLOCK_KEY is ever written — both call sites (here and refresh()'s
  // tap) must go through this exact guard. A write that skips the "only if higher" check can
  // silently lower the recorded high-water mark, which is precisely what an attacker rolling
  // the clock back and then completing one ordinary online refresh would produce.
  private async recordWallClockIfHigher(now: number): Promise<void> {
    const stored = await this.secureKeyService.getItem(MAX_WALL_CLOCK_KEY);
    const maxSeen = stored ? parseInt(stored, 10) : 0;
    if (now > maxSeen) {
      await this.secureKeyService.setItem(MAX_WALL_CLOCK_KEY, String(now));
    }
  }

  private async detectClockRollback(now: number): Promise<boolean> {
    const stored = await this.secureKeyService.getItem(MAX_WALL_CLOCK_KEY);
    const maxSeen = stored ? parseInt(stored, 10) : 0;
    if (now < maxSeen - CLOCK_ROLLBACK_TOLERANCE_MILLIS) {
      return true;
    }
    await this.recordWallClockIfHigher(now);
    return false;
  }
}

function base64ToBytes(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}
